import def from "csi/def";
import { View } from "csi/def/View";
import html from "csi/html";
import locale from "csi/locale";
import reflection from "csi/reflection";
import { sessionStore } from "csi/state/rootStore";
import GroupNodes from "csi/store/GroupNodes";
import csiTable from "csi/table";
import { C8Node, cnl } from "csi/typings";
import csiView from "csi/view";
import Result from "csi/view/_query/Result";
import domConstruct from "dojo/dom-construct";
import NodeList from "dojo/_base/NodeList";
import _ from "lodash";
import { computed } from "mobx";
import { TableWidget } from "./Widget";

type CompareResultMap = Record<string, Result[]>;

const REPORT_KEY = "__REPORT__";
const CSS = "csi-table-compare";
const CSS_NEW = "csi-table-compare-new";
const CSS_OLD = "csi-table-compare-old";

const wrap0 = "<div></div>";
const wrap1 = `<div class="${CSS} ${CSS_OLD}"></div>`;

/** Special value for matrix comparison key indicating to use column index mapping */
export const COMPARE_KEY_INDEX = "__INDEX__";

/** Find cell with given compare key among given matrix rows */
function _findMatrixCell(compareKey: string, matrixRows: HTMLElement[]): HTMLElement {
	const selectors = [html.getDataAttributeSelector(html.TABLE_CELL_COMPARE_KEY, compareKey)];
	let td: HTMLElement;

	matrixRows.some((row) => {
		td = html.queryDom(selectors, row, true);
		return !!td;
	});

	return td;
}

/** See if cell holds a checkbox, and if so, what value it has */
function _getCheckboxValue(td: HTMLElement): string | null {
	const cb = td.querySelector(".dijitCheckBox");
	if (cb) {
		return String(!!cb.classList.contains("dijitChecked"));
	}

	return null;
}

/** Compare a new view with the current one */
export async function createCompareData(currentView: TableWidget, compareNode: C8Node): Promise<TableComparisonData | null> {
	const { cacheView, compareOptions, defView, query } = currentView;
	const currentNode = currentView.getViewNode();

	// no comparison is needed if same node
	if (currentNode.$URL === compareNode.$URL) {
		return null;
	}

	// create view definition
	const compareViewDef = def.getNodeViewDef(currentNode, defView.m_id, true) as View;

	// make sure comparison view is read-only
	compareViewDef.setReadOnly(true);

	const customParams = _.pick(currentView, compareOptions);
	const viewParams: Partial<TableWidget> = {
		bSupportsExpandLevelOption: true,
		cacheView,
		dynamicColumnWidths: true,
		hideFooter: true,
		hideHeader: true,
		isComparing: true,
		query,
		...customParams,
	};

	if (!sessionStore.disableCompareRelativeToOrigin) {
		viewParams.relativeQueryContextUrl = currentNode.$URL;
	}

	// create comparison view with same query as this view
	const compareView = csiView.newView(compareViewDef, compareNode, viewParams, true) as TableWidget;
	compareView.placeAt(html.create("div"));

	// render full view
	await compareView.startup();
	await compareView.expand();

	return new TableComparisonData(currentView, compareView);
}

export class TableComparisonData {
	readonly CSS_NEW = CSS_NEW;
	readonly CSS_OLD = CSS_OLD;

	/** text to represent a blank value in comparison view */
	readonly blankValue: string;

	/** map of column url in current view to one in the comparison view */
	readonly columnUrlMap: Record<cnl, cnl> = {};

	/** table view that is being compared against current one. */
	readonly compareView: TableWidget;

	/** map from compare id to query result of comparison view */
	private readonly compareResultMap: CompareResultMap;

	/** If yes, match colorways by colorway name instead of color spec */
	get compareByColorwayName(): boolean {
		const { compareView, currentView } = this;
		const viewParent = currentView.cacheView.get(currentView.getViewNode().__Parent__);
		const compareViewParent = compareView.cacheView.get(compareView.getViewNode().__Parent__);

		const BOMCopyColorsByProductColorName = reflection.getNodeSetting(viewParent, "BOMCopyColorsByProductColorName");
		const compareBOMCopyColorsByProductColorName = reflection.getNodeSetting(compareViewParent, "BOMCopyColorsByProductColorName");
		return BOMCopyColorsByProductColorName && compareBOMCopyColorsByProductColorName;
	}

	/** current table view in tab */
	readonly currentView: TableWidget;

	constructor(currentView: TableWidget, compareView: TableWidget) {
		this.blankValue = locale.resource("BlankValue");
		this.compareView = compareView;
		this.currentView = currentView;
		this.compareResultMap = this.mapComparisonResults(compareView);
	}

	/** return object that maps the index of cells in compareView to the corresponding index in current view */
	@computed get indexMap(): Record<number, number> {
		const { columnUrlMap, compareView, currentView } = this;

		// create map in current view from spec id to index
		const specToIndex = {};

		compareView.columnData.traverse(true, (c, sc, i, n) => {
			const spec = sc || c;

			if (!c.isActionsColumn()) {
				const key = spec.getKey() || "";
				const url = c.columnUrl || "";
				const id = `${url}_${key}`;
				specToIndex[id] = n;
			}
		});

		// create map of index in compareView back to index of the current view
		const map = {};

		currentView.columnData.traverse(true, (c, sc, i, n) => {
			const spec = sc || c;

			if (!c.isActionsColumn()) {
				const { columnUrl } = c;
				const url = columnUrlMap[columnUrl] || "";

				if (!columnUrl || url) {
					const key = spec.getKey() || "";
					const id = `${url}_${key}`;
					map[n] = specToIndex[id];
				}
			}
		});

		return map;
	}

	/** true if current and compare nodes are revisions under the same master node */
	get isSameMaster(): boolean {
		const { compareView, currentView } = this;
		const currentNode = currentView.getViewNode();
		const compareNode = compareView.getViewNode();
		return reflection.isRevisionNode(currentNode) && (currentNode.Master === compareNode.Master);
	}

	/**
	 * Add column url mapping; can be used by column matrices to indicate simple mapping between columns by URL.
	 * Use this in conjunction with returning COMPARE_KEY_INDEX from getCompareKeyForMergedCells to use the mapping
	 * rather than lookup by a custom key.
	 */
	addColumnUrlMapping = (currentColumnUrl: cnl, compareColumnUrl: cnl): void => {
		this.columnUrlMap[currentColumnUrl] = compareColumnUrl;
	};

	/** add CSS to elements only in current view */
	addNewStyling = (element: HTMLElement): void => {
		element.classList.add(CSS, CSS_NEW);
	};

	/** add CSS to elements only in comparison view */
	addOldStyling = (element: HTMLElement): void => {
		element.classList.add(CSS, CSS_OLD);
	};

	/** Create group key for part map */
	private createMapKey = (qr: Result): string | undefined => {
		const { createMapKey, currentView, isSameMaster } = this;
		const { bAction, id, parentResult } = qr;
		let { comparisonKey } = qr;

		if (!bAction && (comparisonKey === undefined)) {
			if (qr.isRoot()) {
				comparisonKey = id;
			} else {
				let key: string;

				if (qr.isSiblingReport()) {
					key = REPORT_KEY;
				} else {
					key = qr.getGroupValue() || currentView.getCompareKeyForResult(qr, isSameMaster) || "";
				}

				comparisonKey = `${createMapKey(parentResult)}_${key}`;
			}

			qr.comparisonKey = comparisonKey;
		}

		return comparisonKey;
	};

	/**
	 * Create a map of results from comparison view by a "unique" key; it's possible several results use
	 * the same key
	 */
	private mapComparisonResults(compareView: TableWidget): CompareResultMap {
		return _.groupBy(compareView.getQueryResults(), this.createMapKey);
	}

	private augmentCells(td0: HTMLElement, td1: HTMLElement, firstCell: boolean, isOld: boolean): void {
		const { CSS_NOT_APPLICABLE } = html;
		const { CSS_OLD, blankValue } = this;

		// ignore hierarchy icon in first column
		let bHierarchy0 = false;
		let bHierarchy1 = false;

		if (firstCell) {
			if (csiTable.getHierarchyElement(td0)) {
				td0 = td0.lastChild as HTMLElement;
				bHierarchy0 = true;
			}

			if (csiTable.getHierarchyElement(td1)) {
				td1 = td1.lastChild as HTMLElement;
				bHierarchy1 = true;
			}
		}

		// old rows should only display comparison content and not any existing content
		if (isOld) {
			html.clear(td0);
		}

		let text0 = td0.innerText;
		let text1 = td1.innerText;

		// check for special case of checkboxes
		const booleanText0 = _getCheckboxValue(td0);
		const booleanText1 = _getCheckboxValue(td1);

		if (booleanText0 || booleanText1) {
			text0 = booleanText0;
			text1 = booleanText1;
		}

		if (text0 != text1) {
			// do not count "add an image" styling as different from blank
			if (td0.lastChild && (td0.lastChild as HTMLElement).classList?.contains("csi-drop-msg") && text1 == "") {
				return;
			}

			// necessary to avoid changing the compare view, since it may be copied to report rows multiple times
			td1 = td1.cloneNode(true) as HTMLElement;

			if (!text1 || booleanText1) {
				// if there was no previous value, use boolean or blank text
				html.clear(td1);
				html.text(td1, booleanText1 || blankValue);
			}

			if (bHierarchy0) {
				// handle first cell differently due to hierarchy icon
				if (bHierarchy1) {
					td1.classList.add(CSS, CSS_OLD);
				} else {
					(new NodeList([td1]) as any).wrapInner(wrap1);
					td1 = td1.firstChild as HTMLElement;
				}

				domConstruct.place(td1, td0, "before");
			} else {
				// place old value before current value
				(new NodeList([td0]) as any).wrapInner(wrap0);
				(new NodeList([td1]) as any).wrapInner(wrap1);
				domConstruct.place(td1.firstChild, td0, "first");
			}
		}

		// if matrix column from comparison view, then remove N/A CSS
		if (!td1.classList.contains(CSS_NOT_APPLICABLE) && (isOld || td0.classList.contains(CSS_OLD))) {
			td0.classList.remove(CSS_NOT_APPLICABLE);
		}
	}

	/** Compare and augment a child row */
	compareAndAugmentRow(qr: Result, row: HTMLElement): void {
		const { compareView, indexMap } = this;
		const comparisonResult = this.getMatchingQueryResult(qr);
		const { bCompareNode } = qr;
		if (comparisonResult) {
			const compareRow = compareView.getRefNodeById(comparisonResult.id);
			let matrixRows: HTMLElement[];

			row.childNodes.forEach((td0: HTMLElement, i: number) => {
				if (qr.isGroup() && i == 0) {
					return;
				}
				if (td0.classList.contains(csiTable.CSS_ACTION_COLUMN)) {
					return;
				}

				const compareKey = html.getDataAttribute(td0, html.TABLE_CELL_COMPARE_KEY) as string;
				let td1: HTMLElement;
				let isCellOld = false;

				if ((compareKey == null) || (compareKey === COMPARE_KEY_INDEX)) {
					// get corresponding index in comparison view - it only changes due to (column) matrices
					i = indexMap[i];
					td1 = compareRow.childNodes[i] as HTMLElement;
				} else if (compareKey) {
					// if a compare key exists, then need to find cell with the same key in the comparison view
					matrixRows ||= csiTable.getMergedRows(compareRow);
					td1 = _findMatrixCell(compareKey, matrixRows);

					if (td1) {
						isCellOld = td0.classList.contains(CSS_OLD);
					} else {
						td0.classList.add(CSS_NEW);
					}
				}

				if (td1) {
					this.augmentCells(td0, td1, !i, bCompareNode || isCellOld);
				}
			});

			// add old styling if this is a compare node
			if (bCompareNode) {
				this.addOldStyling(row);
			}
		} else {
			// does not exist in compare view, so it is new
			this.addNewStyling(row);
		}
	}

	/** return copy of node in comparison view that will have the same part map key */
	copyComparisonResultNode(comparisonResult: Result): C8Node {
		const { currentView: { cacheView } } = this;
		const comparisonNode = comparisonResult.getNode();
		let node: C8Node;

		if (comparisonResult.isGroup()) {
			node = GroupNodes.createClientNode(
				null,
				"",
				comparisonNode.$Name,
				comparisonResult.getGroupName(),
				comparisonResult.getGroupValue(),
			);

			cacheView.add(node);
		} else {
			// use existing node for non-groups
			node = comparisonNode;
		}


		return node;
	}

	/** Return results from comparison view that are under the same parent but not in the current view */
	findComparisonOnlyResults(qrParent: Result): Result[] {
		const comparisonParent = this.getMatchingQueryResult(qrParent);

		if (_.isEmpty(comparisonParent?.childResults)) {
			return [];
		}

		const usedKeys = new Set<string>();

		qrParent.childResults?.forEach((qr) => {
			const mapKey = this.createMapKey(qr);
			usedKeys.add(mapKey);
		});

		return comparisonParent.childResults.filter((qr) => {
			const mapKey = this.createMapKey(qr);
			return !usedKeys.has(mapKey);
		});
	}

	/** 
	 * Return query result from comparison view that matches a result from the current view;
	 * it's possible that several results use the same key, so need to ensure that each result
	 * is used only once in mapping to the current view. Once a result is matched with another,
	 * then the result cannot be reallocated to another. This is so that as the current view is edited,
	 * it will always match against the same row as initially so as to not keep re-matching against
	 * other results.
	 */
	getMatchingQueryResult = (qr: Result): Result | undefined => {
		let { comparisonResult } = qr;

		if (comparisonResult === undefined) {
			const key = this.createMapKey(qr);
			const results = this.compareResultMap[key];

			comparisonResult = results?.find((qr) => !qr.comparisonResult);

			if (comparisonResult) {
				// set references both ways so results are permanently matched
				comparisonResult.comparisonResult = qr;
				qr.comparisonResult = comparisonResult;
			} else {
				// explicitly set to null so it will not be looked for again
				qr.comparisonResult = null;
			}
		}

		return comparisonResult;
	};

	/** detect if given element has new styling */
	hasNewStyling = (element: HTMLElement): boolean => {
		return element.classList.contains(CSS_NEW);
	};

	/** detect if given element has new styling */
	hasOldStyling = (element: HTMLElement): boolean => {
		return element.classList.contains(CSS_OLD);
	};

	/** unmark matching query result so it can be matched again */
	releaseMatchingQueryResult = (qr: Result): void => {
		const { comparisonResult } = qr;
		if (comparisonResult) {
			comparisonResult.comparisonResult = undefined;
		}
	};
}
